import { useCallback, useState, type Dispatch, type RefObject, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/shallow';

import { SettingModelSettingType } from 'api/models';
import { executeCopilotActions } from 'app/modules/wallforming/components/copilot/utils/copilotCapabilities';
import {
    applyCopilotWorkspaceActions,
    getCornerSolutions,
    getProposalContextSignature,
    isRecord
} from 'app/modules/wallforming/components/copilot/utils/copilotUtils';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import { isCornerSingleSidePoi } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { PlacementTab } from 'app/modules/wallforming/components/setting-container/types/placementTab';
import { SettingView } from 'app/modules/wallforming/components/setting-container/types/settingView';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { applyPlacementGroups } from 'app/modules/wallforming/components/state-management/placement/utils/applyPlacementGroups';
import { EMPTY_GUID } from 'shared/constants';

import type { CopilotProposal } from 'app/modules/wallforming/components/copilot/types/copilotTypes';

interface CopilotProposalHandlerProps {
    applyingRef: RefObject<boolean>;
    onClose: () => void;
    sessionId: string;
    updateProposal: (proposalId: string, update: Partial<CopilotProposal>) => void;
    setError: Dispatch<SetStateAction<string | null>>;
}

const SYSTEM_ID = import.meta.env.VITE_DEFAULT_SYSTEM_ID;

export const useCopilotProposalHandler = ({
    applyingRef,
    onClose,
    sessionId,
    setError,
    updateProposal
}: CopilotProposalHandlerProps) => {
    const { t } = useTranslation();
    const [isApplying, setIsApplying] = useState(false);
    const {
        setPlacementSolutions,
        setSelectedPlacementSolution,
        setSelectedSettingType,
        setSelectedSettingView,
        setAutomatedPlacementTab,
        setPoiSelection
    } = usePlacementStore(
        useShallow((state) => ({
            setPlacementSolutions: state.setPlacementSolutions,
            setSelectedPlacementSolution: state.setSelectedPlacementSolution,
            setSelectedSettingType: state.setSelectedSettingType,
            setSelectedSettingView: state.setSelectedSettingView,
            setAutomatedPlacementTab: state.setAutomatedPlacementTab,
            setPoiSelection: state.setPoiSelection
        }))
    );

    const applyProposal = useCallback(
        async (proposal: CopilotProposal) => {
            if (applyingRef.current || !proposal.accepted || proposal.applied || proposal.rejected) {
                return;
            }
            applyingRef.current = true;
            setIsApplying(true);

            const reportApplyError = (applyError: unknown) => {
                setError(applyError instanceof Error ? applyError.message : t('copilotApplyError'));
            };

            try {
                const currentState = usePlacementStore.getState();
                if (
                    getProposalContextSignature({
                        sessionId,
                        systemId: SYSTEM_ID,
                        workspaceRevision: currentState.workspaceRevision
                    }) !== proposal.contextSignature
                ) {
                    setError(t('copilotStaleContextError'));
                    return;
                }

                switch (proposal.toolName) {
                    case 'propose_application_actions':
                        try {
                            if (!isRecord(proposal.result)) {
                                throw new Error(t('copilotInvalidActionError'));
                            }
                            await executeCopilotActions(proposal.result.actions);
                            updateProposal(proposal.id, { applied: true });
                            setError(null);
                        } catch (applyError) {
                            reportApplyError(applyError);
                        }
                        return;
                    case 'solve_placement_workflow':
                    case 'auto_form_building':
                    case 'solve_panel_placement':
                    case 'solve_connector_placement':
                        try {
                            applyPlacementGroups(proposal.result);
                            updateProposal(proposal.id, { applied: true });
                            setError(null);
                        } catch (applyError) {
                            reportApplyError(applyError);
                        }
                        return;
                    case 'propose_workspace_actions':
                        try {
                            applyCopilotWorkspaceActions(proposal.result, currentState);
                        } catch (applyError) {
                            reportApplyError(applyError);
                            return;
                        }
                        updateProposal(proposal.id, { applied: true });
                        setError(null);
                        return;
                    case 'solve_corner_placement': {
                        const solutions = getCornerSolutions(proposal.result);
                        if (!solutions?.length) {
                            setError(t('copilotInvalidCornerError'));
                            return;
                        }

                        const targetIndex = currentState.pois.findIndex(
                            ({ poi }) => poi.poiGuid === proposal.poiId && isCornerSingleSidePoi(poi)
                        );
                        if (targetIndex < 0 || currentState.isViewMode) {
                            setError(t('copilotStaleContextError'));
                            return;
                        }

                        await setPoiSelection(POIFilterType.Corner, targetIndex);
                        const selectedState = usePlacementStore.getState();
                        const selectedIndex = selectedState.poiSelection.current.index;
                        if (
                            selectedIndex === undefined ||
                            selectedState.pois[selectedIndex]?.poi.poiGuid !== proposal.poiId ||
                            selectedState.pois[selectedIndex]?.poi.poiGuid === EMPTY_GUID
                        ) {
                            setError(t('copilotStaleContextError'));
                            return;
                        }

                        setPlacementSolutions(solutions);
                        setSelectedPlacementSolution(0);
                        setSelectedSettingType(SettingModelSettingType.Corners);
                        setAutomatedPlacementTab(PlacementTab.SOLUTIONS);
                        setSelectedSettingView(SettingView.AUTOMATED_PLACEMENT);
                        updateProposal(proposal.id, { applied: true });
                        onClose();
                        return;
                    }
                    default:
                        return;
                }
            } finally {
                applyingRef.current = false;
                setIsApplying(false);
            }
        },
        [
            applyingRef,
            onClose,
            sessionId,
            setAutomatedPlacementTab,
            setError,
            setPlacementSolutions,
            setPoiSelection,
            setSelectedPlacementSolution,
            setSelectedSettingType,
            setSelectedSettingView,
            t,
            updateProposal
        ]
    );

    return { applyProposal, isApplying };
};
